import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { identifyPostHogUser, resetPostHogIdentity } from "./posthog";

// Load Google Identity Services script once
function loadGsiScript() {
  if (document.getElementById("google-gsi-script")) return;
  const s = document.createElement("script");
  s.id = "google-gsi-script";
  s.src = "https://accounts.google.com/gsi/client";
  s.async = true;
  s.defer = true;
  document.head.appendChild(s);
}

const AuthCtx = createContext(null);
const TOKEN_KEY = "tcrm_token";
const USER_KEY = "tcrm_user";
// How often an open tab re-checks whether its token is worth renewing.
const RENEW_CHECK_MS = 30 * 60 * 1000;

/** Read a JWT's claims without verifying it — the server is the authority. */
function tokenClaims(token) {
  try {
    const [, payload] = String(token).split(".");
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** True once the token is past half its lifetime, or already expired. */
function shouldRenewToken(token) {
  const claims = tokenClaims(token);
  if (!claims?.exp) return false;
  // Impersonation tokens are deliberately short-lived; the server refuses to
  // extend them, so don't bother asking.
  if (claims.renewable === false) return false;
  const expMs = claims.exp * 1000;
  const issuedMs = claims.iat ? claims.iat * 1000 : null;
  if (!issuedMs) return expMs - Date.now() < 24 * 60 * 60 * 1000;
  return Date.now() >= issuedMs + (expMs - issuedMs) / 2;
}

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

function writeAuthSession({ token, user }) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearAuthSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  // Only block the app when there is nothing to go on. With a token and a
  // stored user we already know who this is, so the app renders and its pages
  // start fetching immediately while /auth/me confirms in the background.
  // Blocking on that call put a whole round trip in front of every refresh
  // before any page could even ask for its data — and if the session turns out
  // to be stale, the 401 interceptor clears it and redirects anyway.
  const [loading, setLoading] = useState(
    () => !!localStorage.getItem(TOKEN_KEY) && !readStoredUser(),
  );

  useEffect(() => {
    // Pre-load Google Identity Services so the sign-in button renders fast.
    if (process.env.REACT_APP_GOOGLE_CLIENT_ID) loadGsiScript();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      // Always fetch fresh user data from the server so fields like
      // is_superadmin are always current, even if a cached user exists.
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem(USER_KEY, JSON.stringify(r.data));
      }).catch((error) => {
        const status = error?.response?.status;
        if (status === 401) {
          clearAuthSession();
          setUser(null);
          return;
        }
        // Keep the last-known session during transient boot failures so
        // opening a new tab does not force a fresh login.
        setUser((current) => current || readStoredUser());
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // Intentionally run once on mount to bootstrap auth; subsequent auth changes
    // are driven by login/register/logout which set `user` directly.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && ![TOKEN_KEY, USER_KEY].includes(event.key)) return;
      const nextToken = localStorage.getItem(TOKEN_KEY);
      const nextUser = readStoredUser();
      if (!nextToken) {
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(nextUser);
      setLoading(false);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    if (loading) return;
    if (user) {
      identifyPostHogUser(user);
    } else if (!localStorage.getItem(TOKEN_KEY)) {
      resetPostHogIdentity();
    }
  }, [loading, user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    writeAuthSession({ token: data.access_token, user: data.user });
    setUser(data.user);
    return data.user;
  }, []);

  const googleLogin = useCallback(async (credential) => {
    const { data } = await api.post("/auth/google", { credential });
    writeAuthSession({ token: data.access_token, user: data.user });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    writeAuthSession({ token: data.access_token, user: data.user });
    setUser(data.user);
    return data.user;
  }, []);

  // Tokens used to expire a fixed period after sign-in with nothing renewing
  // them, so an active user was still logged out on schedule — and noticed it
  // in a newly opened tab, which revalidates immediately. Renew in the
  // background once the token is past half its life.
  const renewingRef = useRef(false);
  const renewSession = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token || renewingRef.current || !shouldRenewToken(token)) return;
    renewingRef.current = true;
    try {
      const { data } = await api.post("/auth/refresh");
      // The session can change while this is in flight — a logout, or a sign-in
      // as someone else. Writing the response back then would resurrect the old
      // session over the new state, so drop it unless the token is still ours.
      if (localStorage.getItem(TOKEN_KEY) !== token) return;
      writeAuthSession({ token: data.access_token, user: data.user });
      setUser(data.user);
    } catch {
      // A 401 is handled by the api interceptor; anything else is transient and
      // gets another attempt on the next check.
    } finally {
      renewingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (loading || !user) return undefined;
    renewSession();
    const timer = setInterval(renewSession, RENEW_CHECK_MS);
    // Background tabs have their timers throttled, so also renew when the tab
    // comes back. `focus` is not gated on visibilityState: the event itself
    // means the tab is in front, and gating it made the listener a no-op.
    const onVisibilityChange = () => { if (document.visibilityState !== "hidden") renewSession(); };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", renewSession);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", renewSession);
    };
  }, [loading, user, renewSession]);

  const logout = useCallback(() => {
    clearAuthSession();
    // Clear any active impersonation session so the return token can't be reused.
    localStorage.removeItem("tcrm_return_token");
    localStorage.removeItem("tcrm_return_name");
    resetPostHogIdentity();
    setUser(null);
  }, []);

  /** Re-fetch user from /auth/me (e.g. after plan upgrade) and update state + cache. */
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      localStorage.setItem(USER_KEY, JSON.stringify(data));
      setUser(data);
      return data;
    } catch {
      // silently fail — auth interceptor will handle 401s
    }
  }, []);

  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

  const value = useMemo(
    () => ({ user, token, loading, login, googleLogin, register, logout, refreshUser }),
    [user, token, loading, login, googleLogin, register, logout, refreshUser]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
